import React, { useState, useMemo, useCallback } from 'react';
import { StyledTab, StyledTabView } from './tabControl.styled';
import {
    FONT_FAMILY_DEMI,
    BASE_TEXT_PX,
    DARK_GRAY_FONT_COLOR, 
} from 'src/styles/fonts/MCFontConstants';
import { useEffectWithErrorLogging} from 'src/utils/useEffectWithErrorLogging';

const TabControl = (props) => {
    const {
        tabOneTitle = 'item 1',
        tabOneNumber,
        tabOneChildren = <></>,
        tabOneId='',
        tabTwoTitle = 'item 2',
        tabTwoNumber,
        tabTwoChildren = <></>,
        tabTwoId='',
        tabThreeTitle = 'item 3', 
        tabThreeNumber,
        tabThreeChildren = <></>, 
        tabThreeId='',
        showTabThree = false,
        tabViewHeight = '40px',
        tabViewWidth = '40%',
        tabViewWidthMediumDevice = '60%',
        tabViewWidthLargeDevice = '100%',
        widthForMobileScreen,
        tabViewMargin,
        marginTop,
        marginBottom,
        tabFontFamily = FONT_FAMILY_DEMI,
        tabFontSize = BASE_TEXT_PX,
        tabFontColor = DARK_GRAY_FONT_COLOR,
        inactiveTabFontColor,
        selectedTabState = () => {},
        currentActiveIndex = 0,
        border,
        mbBorder,
        borderRadius,
        backgroundColor='none',
        activeBGColor,
        disabledBGcolor,
        dkPadding,
        paddingTop,
        paddingBottom,
        tabWidth,
        whiteSpace,
        flexGrow,
        justifyContent,
        isCondensedView,
        useLabel = false,
        tabChanged = false,
        isTabOneNotDoValidation,
        onTabChange = () => {},
        setHasTabChanged = () => {},
        setEquityActiveIndex = () => {},
        id,
    } = props;

    const [activeIndex, setActiveIndex] = useState(currentActiveIndex);

    const tabOneLabel = useMemo(() => {
        if (useLabel) return tabOneTitle;
        if (tabOneNumber === 0) return `${tabOneTitle} (0)`;
        if (tabOneNumber === '' || tabOneNumber === undefined || tabOneNumber === null) return tabOneTitle;
        return `${tabOneTitle} (${tabOneNumber})`;
    }, [tabOneTitle, tabOneNumber, useLabel]);

    const tabTwoLabel = useMemo(() => {
        if (useLabel) return tabTwoTitle;
        if (tabTwoNumber === 0) return `${tabTwoTitle} (0)`;
        if (tabTwoNumber === '' || tabTwoNumber === undefined || tabTwoNumber === null) return tabTwoTitle;
        return `${tabTwoTitle} (${tabTwoNumber})`;
    }, [tabTwoTitle, tabTwoNumber, useLabel]);

    const tabThreeLabel = useMemo(() => { 
        if (useLabel) return tabThreeTitle;
        if (tabThreeNumber === 0) return `${tabThreeTitle} (0)`;
        if (tabThreeNumber === '' || tabThreeNumber === undefined || tabThreeNumber === null) return tabThreeTitle;
        return `${tabThreeTitle} (${tabThreeNumber})`;
    }, [tabThreeTitle, tabThreeNumber, useLabel]);

    const disableTabs = useCallback((number) => {
        switch (number) {
            case undefined:
            case null:
                return false;
            case 0:
                return true;
            default:
                return false;
        }
    }, []);

    const updateSelectedState = useCallback((val) => {
        setHasTabChanged(!tabChanged);
        setActiveIndex(val);
        setEquityActiveIndex(val);
        selectedTabState(val);
        onTabChange(val);
    }, [setHasTabChanged, tabChanged, setEquityActiveIndex, selectedTabState, onTabChange]);

    useEffectWithErrorLogging(() => {
        setActiveIndex(currentActiveIndex);
    }, [currentActiveIndex]);

    const getFirstEnabledTabIndex = useCallback(() => {
        const tabs = [
            { index: 0, number: tabOneNumber },
            { index: 1, number: tabTwoNumber },
            ...(showTabThree ? [{ index: 2, number: tabThreeNumber }] : []),
        ];
        
        const enabledTab = tabs.find(tab => tab.number !== 0);
        return enabledTab ? enabledTab.index : 0;
    }, [tabOneNumber, tabTwoNumber, tabThreeNumber, showTabThree]);

    useEffectWithErrorLogging(() => {
        if (isTabOneNotDoValidation) {
            return;
        }
        // Auto-switch to first enabled tab if current tab is disabled
        const currentTabNumbers = [tabOneNumber, tabTwoNumber, tabThreeNumber];
        const currentTabNumber = currentTabNumbers[activeIndex];
        
        if (currentTabNumber === 0) {
            const firstEnabledIndex = getFirstEnabledTabIndex();
            setActiveIndex(firstEnabledIndex);
            setEquityActiveIndex(firstEnabledIndex);
            selectedTabState(firstEnabledIndex);
        }
    }, [tabOneNumber, tabTwoNumber, tabThreeNumber, isTabOneNotDoValidation, setEquityActiveIndex, activeIndex, getFirstEnabledTabIndex, selectedTabState]);

    return (
        <StyledTabView
            id={id}
            widthForMobileScreen={widthForMobileScreen}
            disabledBGcolor={disabledBGcolor}
            isCondensedView={isCondensedView}
            backgroundColor={backgroundColor}
            activeBGColor={activeBGColor}
            tabViewHeight={tabViewHeight}
            tabViewWidth={tabViewWidth}
            tabFontFamily={tabFontFamily}
            tabFontSize={tabFontSize}
            tabFontColor={tabFontColor}
            inactiveTabFontColor={inactiveTabFontColor}
            activeIndex={activeIndex}
            border={border}
            mbBorder={mbBorder}
            borderRadius={borderRadius}
            onTabChange={(e) => updateSelectedState(e.index)}
            tabViewWidthMediumDevice={tabViewWidthMediumDevice}
            tabViewWidthLargeDevice={tabViewWidthLargeDevice}
            whiteSpace={whiteSpace}
            justifyContent={justifyContent}
            tabViewMargin={tabViewMargin}
            tabWidth={tabWidth}
            flexGrow={flexGrow}
            dkPadding={dkPadding}
            paddingTop={paddingTop}
            paddingBottom={paddingBottom}
            marginTop={marginTop}
            marginBottom={marginBottom}
        >
            {[
                <StyledTab
                    key="tab1"
                    id={tabOneId}
                    header={tabOneLabel}
                    disabled={disableTabs(tabOneNumber)}
                >
                    {tabOneChildren}
                </StyledTab>,
                <StyledTab
                    key="tab2"
                    id={tabTwoId}
                    header={tabTwoLabel}
                    disabled={disableTabs(tabTwoNumber)}
                >
                    {tabTwoChildren}
                </StyledTab>,
                showTabThree && (
                    <StyledTab
                        key="tab3"
                        id={tabThreeId}
                        header={tabThreeLabel}
                        disabled={disableTabs(tabThreeNumber)}
                    >
                        {tabThreeChildren}
                    </StyledTab>
                ),
            ].filter(Boolean)}
        </StyledTabView>
    );
};

export default TabControl;